import React from "react";
import { resolveCard, renderMajor, renderMinor } from "@/lib/tarotFaces";

/**
 * CardFace — the standardized AuraVeil proprietary card face.
 *
 * Style: Minimalist Sacred Geometry
 *   ▸ Deep matte black base (#080808)
 *   ▸ Thin Soft Gold (#D4AF37) frame + inner sacred border
 *   ▸ Elegant line-art symbol (SVG stroke-only)
 *   ▸ Cormorant Garamond italic title at the bottom
 *
 * Props:
 *   title    string          — full tarot title, e.g. "The Magician", "Three of Cups"
 *   upright  bool  (true)    — rotates the symbol 180° when false
 *   size     number          — pixel width (aspect 3:4 auto-height)
 *   className / style        — pass-through
 */
export default function CardFace({
  title,
  upright = true,
  size = 180,
  className = "",
  style = {},
}) {
  const card = resolveCard(title);
  const width = size;
  const height = Math.round(size * (4 / 3));

  const gold = "#D4AF37";
  const goldDim = "rgba(212,175,55,0.35)";

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{
        width,
        height,
        background: "#080808",
        border: `1px solid ${goldDim}`,
        boxShadow: "0 22px 60px rgba(0,0,0,0.55)",
        ...style,
      }}
      data-card-face={title}
    >
      <svg
        viewBox="0 0 120 160"
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height="100%"
        style={{ display: "block" }}
      >
        {/* Sacred inner frame — thin gold rectangle inset from the border */}
        <rect
          x="6"
          y="6"
          width="108"
          height="148"
          rx="4"
          ry="4"
          fill="none"
          stroke={goldDim}
          strokeWidth="0.6"
        />
        {/* Corner ornaments — small sacred marks */}
        <CornerMarks color={gold} />

        {/* Symbol chamber */}
        <g
          transform="translate(60, 60)"
          fill="none"
          stroke={gold}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transformOrigin: "60px 60px",
          }}
        >
          <g transform={upright ? "" : "rotate(180)"}>
            {card?.type === "major" && renderMajor(card.name)}
            {card?.type === "minor" && (
              <g transform="translate(-60, -60)">
                {renderMinor(card.rank, card.suit)}
              </g>
            )}
          </g>
        </g>

        {/* Divider above title */}
        <line
          x1="30"
          y1="128"
          x2="90"
          y2="128"
          stroke={goldDim}
          strokeWidth="0.5"
        />

        {/* Card title in Cormorant Garamond */}
        <text
          x="60"
          y="142"
          textAnchor="middle"
          fill={gold}
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontWeight: 400,
            letterSpacing: "0.08em",
          }}
          fontSize={fontSizeFor(title)}
        >
          {title}
        </text>
      </svg>
    </div>
  );
}

function fontSizeFor(title) {
  // Long titles need to shrink to fit
  if (!title) return 8;
  if (title.length > 20) return 6.4;
  if (title.length > 16) return 7.2;
  if (title.length > 12) return 8;
  return 9;
}

function CornerMarks({ color }) {
  const c = color;
  const r = 0.9;
  return (
    <g fill={c}>
      <circle cx="12" cy="12" r={r} />
      <circle cx="108" cy="12" r={r} />
      <circle cx="12" cy="148" r={r} />
      <circle cx="108" cy="148" r={r} />
    </g>
  );
}
